import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Save, Globe, Eye } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  { value: 'validation', label: 'Validation' },
  { value: 'product', label: 'Product' },
  { value: 'founder', label: 'Founder Mindset' },
  { value: 'scaling', label: 'Scaling' },
];

export default function BlogEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: 'validation',
    status: 'draft'
  });

  const fetchBlog = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/blogs/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blog = await response.json();
        setFormData({
          title: blog.title,
          excerpt: blog.excerpt,
          content: blog.content,
          category: blog.category,
          status: blog.status
        });
      } else {
        toast.error('Blog not found');
        navigate('/admin/blog');
      }
    } catch (error) {
      console.error('Failed to fetch blog:', error);
      toast.error('Failed to load blog');
    } finally {
      setLoading(false);
    }
  }, [id, token, navigate]);

  useEffect(() => {
    if (!isNew) {
      fetchBlog();
    }
  }, [id, isNew, fetchBlog]);

  const handleSave = async (publish = false) => {
    if (!formData.title || !formData.excerpt || !formData.content) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        status: publish ? 'published' : formData.status
      };

      let response;
      if (isNew) {
        response = await fetch(`${API_URL}/api/blogs`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(data)
        });
      } else {
        response = await fetch(`${API_URL}/api/blogs/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(data)
        });
      }

      if (response.ok) {
        const savedBlog = await response.json();
        toast.success(publish ? 'Blog published!' : 'Blog saved');
        if (isNew) {
          navigate(`/admin/blog/${savedBlog.id}`);
        }
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save blog');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!formData.title || !formData.excerpt || !formData.content) {
      toast.error('Please fill in all required fields before publishing');
      return;
    }

    if (isNew) {
      handleSave(true);
    } else {
      setSaving(true);
      try {
        // First save any changes
        await fetch(`${API_URL}/api/blogs/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(formData)
        });

        // Then publish
        const response = await fetch(`${API_URL}/api/blogs/${id}/publish`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          toast.success('Blog published!');
          setFormData({ ...formData, status: 'published' });
        }
      } catch (error) {
        toast.error('Failed to publish blog');
      } finally {
        setSaving(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/blog')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-serif font-medium text-foreground">
              {isNew ? 'New Blog Post' : 'Edit Blog Post'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {formData.status === 'published' ? 'Published' : 'Draft'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => handleSave(false)} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            Save Draft
          </Button>
          {formData.status !== 'published' && (
            <Button onClick={handlePublish} disabled={saving}>
              <Globe className="h-4 w-4 mr-2" />
              Publish
            </Button>
          )}
          {formData.status === 'published' && !isNew && (
            <Button variant="outline" asChild>
              <a href={`/blog/${id}`} target="_blank">
                <Eye className="h-4 w-4 mr-2" />
                View Live
              </a>
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-6">
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Enter blog title..."
              className="text-lg"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="category">Category *</Label>
              <Select 
                value={formData.category} 
                onValueChange={(v) => setFormData({ ...formData, category: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="excerpt">Excerpt *</Label>
            <Textarea
              id="excerpt"
              value={formData.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              placeholder="Brief summary of the post (shown in listings)..."
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="content">Content * (HTML supported)</Label>
            <Textarea
              id="content"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder="Write your blog content here... You can use HTML tags like <h2>, <p>, <ul>, <blockquote>, etc."
              rows={20}
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Supported HTML: &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;, &lt;blockquote&gt;, &lt;strong&gt;, &lt;em&gt;
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
